import {format, formatDistanceToNow} from 'date-fns';
import {type Item} from '@/api/items';
import {
  AGE_RESTRICTED_FIELD_NAMES,
  AI_GENERATED_FIELD_NAMES,
  CHANNEL_VIDEO_COUNT_FIELD_NAMES,
  CONTENT_WARNING_FIELD_NAMES,
  CONTENT_WARNING_REASON_FIELD_NAMES,
  DURATION_FIELD_NAMES,
  COUNTRY_FIELD_NAMES,
  FOLLOWER_FIELD_NAMES,
  JOINED_DATE_FIELD_NAMES,
  LAST_POST_DATE_FIELD_NAMES,
  LIKE_COUNT_FIELD_NAMES,
  MADE_FOR_KIDS_FIELD_NAMES,
  isChannelMonetized,
  SAFE_FOR_MINORS_FIELD_NAMES,
  SAVE_COUNT_FIELD_NAMES,
  SUBSCRIBER_FIELD_NAMES,
  VERIFIED_FIELD_NAMES,
  VERIFIED_MERCHANT_FIELD_NAMES,
  VIEW_COUNT_FIELD_NAMES,
  formatCompactNumber,
  getAutoSelectedLanguageCode,
  getDescription,
  getMetaBoolean,
  getMetaField,
  getMetaFieldAny,
  getMetaFields,
  getTags,
  type ItemMetadataLike
} from '@/lib/itemMetadata';

// How one Item maps onto the card's four areas - media, title, stats, description + tags - for each
// of the five shapes the Item Field Atlas describes. Kept apart from ItemCard so the per-platform
// rules (which field names, which fallbacks) are plain data a test can pin down, and the card itself
// stays layout.

export type ItemVariant = 'video' | 'channel' | 'profile' | 'pin';

// Pinterest pins collected before 9 Oct 2026 may carry content_type "Pinterest Collection", so any
// non-actor Pinterest item is a pin (Atlas, "Pinterest pin").
export function itemVariant(item: Pick<Item, 'platform' | 'content_type'>): ItemVariant {
  const pinterest = item.platform.toLowerCase() === 'pinterest';
  if (item.content_type === 'actor') {
    return pinterest ? 'profile' : 'channel';
  }

  return pinterest ? 'pin' : 'video';
}

export const VARIANT_LABEL: Record<ItemVariant, string> = {
  video: 'Video',
  channel: 'Channel',
  profile: 'Profile',
  pin: 'Pin'
};

export type ItemStatKind =
  | 'ageRestricted'
  | 'contentWarning'
  | 'safeForMinors'
  | 'aiGenerated'
  | 'views'
  | 'likes'
  | 'saves'
  | 'audience'
  | 'following'
  | 'videos'
  | 'pins'
  | 'boards'
  | 'country'
  | 'domain'
  | 'verified'
  | 'verifiedMerchant'
  | 'monetized'
  | 'madeForKids';

export type ItemStat = {
  readonly kind: ItemStatKind;
  // What the value means, for the icon's accessible name and the hover tooltip.
  readonly label: string;
  readonly value: string;
  // Printed after the value ("48K subscribers") where the icon alone would be ambiguous.
  readonly suffix?: string;
  // Colour is kept for what an analyst must not miss - a risk the platform itself reported - and
  // nothing else, so it still means something when it appears.
  readonly tone?: 'default' | 'destructive';
  // The tooltip, when it says more than the label (e.g. a content warning's reason).
  readonly detail?: string;
};

export type ItemDateLine = {
  readonly text: string;
  // Relative form, for the hover title.
  readonly title: string;
};

export type ItemCardModel = {
  readonly variant: ItemVariant;
  // Untranslated; the card swaps in a translation when one is shown.
  readonly name: string;
  readonly handle: string | null;
  // "HH:MM:SS" for the media's corner tag; null for anything that isn't a video.
  readonly duration: string | null;
  readonly date: ItemDateLine | null;
  readonly stats: readonly ItemStat[];
  // The item's declared language as a name ("French"), shown with the Translate link; null when the
  // platform didn't say.
  readonly language: string | null;
  readonly description: string;
  readonly tags: readonly string[];
};

/**
 True or false when the platform said so, null when it didn't: absent is not false (Atlas, trap 8),
 so a card shows nothing rather than a negative for a field the crawler never wrote.
 */
export function getMetaBooleanState(
  metadata: ItemMetadataLike,
  candidateFieldNames: string[]
): boolean | null {
  const field = getMetaFields(metadata).find((f) =>
    candidateFieldNames.includes(f.FieldName.toLowerCase())
  );
  return typeof field?.Content === 'boolean' ? field.Content : null;
}

/**
 YouTube channel Keywords: space-separated, with a multi-word keyword in double quotes
 (`harbour "slow tv" boats`) - not the comma list a video's Tags are.
 */
export function getKeywords(metadata: ItemMetadataLike): string[] {
  const raw = getMetaField(metadata, 'Keywords');
  return [...raw.matchAll(/"([^"]*)"|(\S+)/g)]
    .map((match) => (match[1] ?? match[2] ?? '').trim())
    .filter(Boolean);
}

// Scrapy's date text comes as ISO 8601, "2014-03-02 00:00:00" or "2026-09-21 07:14:02+00:00" - the
// space form isn't one every engine parses, so it is normalised to a T first.
export function parseItemDate(raw: string | null | undefined): Date | null {
  if (!raw) {
    return null;
  }

  const date = new Date(raw.trim().replace(/(?<=^\d{4}-\d{2}-\d{2}) /, 'T'));
  return Number.isNaN(date.getTime()) ? null : date;
}

function dateLine(prefix: string, raw: string | null | undefined): ItemDateLine | null {
  const date = parseItemDate(raw);
  return date
    ? {
        text: `${prefix} ${format(date, 'd MMM yyyy')}`,
        title: formatDistanceToNow(date, {addSuffix: true})
      }
    : null;
}

const languageNames = new Intl.DisplayNames(['en'], {type: 'language'});

// "en" → "English"; a value that isn't a language code ("Not specified", "English") is shown as is.
export function languageLabel(raw: string): string {
  try {
    return languageNames.of(raw) ?? raw;
  } catch {
    return raw;
  }
}

/** The host of a link, for showing where an untrusted URL points without printing all of it. */
export function linkHost(raw: string): string | null {
  try {
    return new URL(raw).host.replace(/^www\./, '') || null;
  } catch {
    return null;
  }
}

type CountSpec = {
  readonly kind: ItemStatKind;
  readonly label: string;
  readonly names: string[];
  readonly suffix?: string;
};

function count(
  metadata: ItemMetadataLike,
  {kind, label, names, suffix}: CountSpec
): ItemStat | null {
  const value = formatCompactNumber(getMetaFieldAny(metadata, names));
  return value ? {kind, label, value, ...(suffix ? {suffix} : {})} : null;
}

function aiGenerated(metadata: ItemMetadataLike): ItemStat | null {
  // The platform's own "altered or synthetic content" label. Only a positive is worth showing:
  // false and absent both mean no label, and neither is an assessment by us.
  return getMetaBooleanState(metadata, AI_GENERATED_FIELD_NAMES) === true
    ? {kind: 'aiGenerated', label: 'AI generated', value: 'AI generated'}
    : null;
}

function safeForMinors(metadata: ItemMetadataLike): ItemStat | null {
  // Not safe is the platform default, not an assessment, so only a positive is worth showing.
  return getMetaBooleanState(metadata, SAFE_FOR_MINORS_FIELD_NAMES) === true
    ? {kind: 'safeForMinors', label: 'Safe for minors', value: 'Safe'}
    : null;
}

// The audio track's language wins over the metadata language, and YouTube's literal "Not specified"
// counts as unknown (see getAutoSelectedLanguageCode).
function language(metadata: ItemMetadataLike): string | null {
  const code = getAutoSelectedLanguageCode(metadata);
  return code ? languageLabel(code) : null;
}

// A Pinterest video pin reports whole seconds; YouTube already writes "HH:MM:SS".
function duration(metadata: ItemMetadataLike): string | null {
  const text = getMetaFieldAny(metadata, DURATION_FIELD_NAMES);
  if (text) {
    return text;
  }

  const seconds = Number(getMetaFieldAny(metadata, ['duration seconds']) || Number.NaN);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }

  const pad = (n: number) => String(Math.floor(n)).padStart(2, '0');
  return `${pad(seconds / 3600)}:${pad((seconds % 3600) / 60)}:${pad(seconds % 60)}`;
}

// Risks the platform reported, which lead the stats line in the destructive tone.
function warnings(metadata: ItemMetadataLike): ItemStat[] {
  const reason = getMetaFieldAny(metadata, CONTENT_WARNING_REASON_FIELD_NAMES);
  return present<ItemStat>([
    getMetaBoolean(metadata, AGE_RESTRICTED_FIELD_NAMES)
      ? {
          kind: 'ageRestricted',
          label: 'Age restricted',
          value: 'Age restricted',
          tone: 'destructive'
        }
      : null,
    getMetaBoolean(metadata, CONTENT_WARNING_FIELD_NAMES)
      ? {
          kind: 'contentWarning',
          label: 'Content warning',
          value: 'Content warning',
          tone: 'destructive',
          ...(reason ? {detail: reason} : {})
        }
      : null
  ]);
}

// Claims about the account, which close the stats line in the ordinary tone. Identity and merchant
// verification are two different claims on Pinterest, so they are named apart (PR #578).
function accountFlags(metadata: ItemMetadataLike, variant: ItemVariant): ItemStat[] {
  const verifiedLabel =
    variant === 'profile' || variant === 'pin' ? 'Identity verified' : 'Verified channel';
  return present<ItemStat>([
    getMetaBoolean(metadata, VERIFIED_FIELD_NAMES)
      ? {kind: 'verified', label: verifiedLabel, value: verifiedLabel}
      : null,
    getMetaBoolean(metadata, VERIFIED_MERCHANT_FIELD_NAMES)
      ? {kind: 'verifiedMerchant', label: 'Verified merchant', value: 'Verified merchant'}
      : null,
    isChannelMonetized(metadata, variant === 'channel' || variant === 'profile')
      ? {kind: 'monetized', label: 'Channel monetised', value: 'Monetised'}
      : null,
    getMetaBoolean(metadata, MADE_FOR_KIDS_FIELD_NAMES)
      ? {kind: 'madeForKids', label: 'Made for kids', value: 'Made for kids'}
      : null
  ]);
}

function present<T>(values: ReadonlyArray<T | null>): T[] {
  return values.filter((value): value is T => value !== null);
}

export function getItemCardModel(item: Item): ItemCardModel {
  const base = getBaseModel(item);
  const isActor = base.variant === 'channel' || base.variant === 'profile';
  return {
    ...base,
    duration: isActor ? null : duration(item.metadata),
    stats: [...warnings(item.metadata), ...base.stats, ...accountFlags(item.metadata, base.variant)]
  };
}

// The per-variant mapping; getItemCardModel adds what every variant shares.
function getBaseModel(item: Item): Omit<ItemCardModel, 'duration'> {
  const {metadata} = item;
  const variant = itemVariant(item);
  const description = getDescription(metadata);
  const handle = item.author_handle ?? null;

  switch (variant) {
    case 'channel': {
      const country = getMetaFieldAny(metadata, COUNTRY_FIELD_NAMES);
      const tags = getTags(metadata);
      return {
        variant,
        name: item.title ?? item.author_name ?? handle ?? item.external_id,
        handle,
        date:
          dateLine('Joined', getMetaFieldAny(metadata, JOINED_DATE_FIELD_NAMES)) ??
          dateLine('Last post', getMetaFieldAny(metadata, LAST_POST_DATE_FIELD_NAMES)),
        stats: present([
          count(metadata, {
            kind: 'audience',
            label: 'Subscribers',
            names: SUBSCRIBER_FIELD_NAMES,
            suffix: 'subscribers'
          }),
          count(metadata, {kind: 'views', label: 'Views', names: VIEW_COUNT_FIELD_NAMES}),
          count(metadata, {
            kind: 'videos',
            label: 'Videos',
            names: CHANNEL_VIDEO_COUNT_FIELD_NAMES,
            suffix: 'videos'
          }),
          country ? {kind: 'country', label: 'Country', value: country} : null
        ]),
        language: language(metadata),
        description,
        tags: tags.length > 0 ? tags : getKeywords(metadata)
      };
    }

    case 'profile': {
      // A Pinterest profile has no Title field: name it from the display name, then the handle.
      return {
        variant,
        name: item.title ?? item.author_name ?? handle ?? item.external_id,
        handle,
        date: dateLine('Last pin saved', getMetaFieldAny(metadata, LAST_POST_DATE_FIELD_NAMES)),
        stats: present([
          count(metadata, {
            kind: 'audience',
            label: 'Followers',
            names: FOLLOWER_FIELD_NAMES,
            suffix: 'followers'
          }),
          count(metadata, {
            kind: 'following',
            label: 'Following',
            names: ['following count'],
            suffix: 'following'
          }),
          count(metadata, {kind: 'pins', label: 'Pins', names: ['pin count'], suffix: 'pins'}),
          count(metadata, {
            kind: 'boards',
            label: 'Boards',
            names: ['board count'],
            suffix: 'boards'
          })
        ]),
        language: language(metadata),
        description,
        tags: []
      };
    }

    case 'pin': {
      const domain = getMetaFieldAny(metadata, ['domain']);
      // Only half of pins carry a Title; the description stands in, and then isn't repeated below.
      const name = item.title ?? (description || item.external_id);
      return {
        variant,
        name,
        handle,
        date: dateLine('Uploaded', item.published_at),
        stats: present([
          count(metadata, {
            kind: 'saves',
            label: 'Saves',
            names: SAVE_COUNT_FIELD_NAMES,
            suffix: 'saves'
          }),
          domain ? {kind: 'domain', label: 'Domain', value: domain} : null
        ]),
        language: language(metadata),
        description: name === description ? '' : description,
        tags: getTags(metadata)
      };
    }

    case 'video': {
      return {
        variant,
        name: item.title ?? item.external_id,
        handle: handle ?? item.author_name ?? null,
        date: dateLine('Uploaded', item.published_at),
        stats: present([
          safeForMinors(metadata),
          aiGenerated(metadata),
          count(metadata, {kind: 'views', label: 'Views', names: VIEW_COUNT_FIELD_NAMES}),
          count(metadata, {kind: 'likes', label: 'Likes', names: LIKE_COUNT_FIELD_NAMES}),
          // A video carries its channel's audience, so the card shows it without the channel crawled.
          count(metadata, {
            kind: 'audience',
            label: 'Subscribers',
            names: SUBSCRIBER_FIELD_NAMES,
            suffix: 'subscribers'
          })
        ]),
        language: language(metadata),
        description,
        tags: getTags(metadata)
      };
    }
  }
}
